import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useModal } from '../context/ModalDialogContext';

export function AssetManager({ user, tenantConfig, selectedTenant, selectedUserContext }) {
  const modal = useModal();
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');
  const isDev = window.location.port === '3000';
  const API_BASE = isDev ? `http://${window.location.hostname}:8000` : '';

  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [hoveredAssetId, setHoveredAssetId] = useState(null);

  // Upload Modal State
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [modalFile, setModalFile] = useState(null);
  const [modalFilePreview, setModalFilePreview] = useState(null);
  const [modalAssetName, setModalAssetName] = useState('');
  const [modalTags, setModalTags] = useState([]);
  const [modalTagInput, setModalTagInput] = useState('');
  const [modalDragActive, setModalDragActive] = useState(false);
  const [modalError, setModalError] = useState('');

  // Universal Top Banner Slide-in Notification Observers
  useEffect(() => {
    if (success) modal.notify(success, 'success');
  }, [success, modal]);

  useEffect(() => {
    if (error) modal.notify(error, 'error');
  }, [error, modal]);

  useEffect(() => {
    if (modalError) modal.notify(modalError, 'error');
  }, [modalError, modal]);

  const modalFileInputRef = useRef(null);

  const SUGGESTED_TAGS = [
    'Coin Face',
    'Logo',
    'Background',
    'Artwork',
    'Banner',
    'Brag Page',
    'Vector',
    'Document'
  ];

  const fetchAssets = () => {
    if (!token) return;
    setLoading(true);

    let url = `${API_BASE}/api/upload?`;
    const params = new URLSearchParams();
    if (selectedUserContext && selectedUserContext !== 'self') {
      params.append('associated_user_id', selectedUserContext);
    }
    if (selectedTenant) {
      params.append('tenant_id', selectedTenant);
    }
    url += params.toString();

    fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Tenant-Domain': window.location.hostname
      }
    })
      .then(res => {
        if (!res.ok) throw new Error("Failed to fetch assets list");
        return res.json();
      })
      .then(data => {
        setAssets(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedTenant, selectedUserContext]);

  // Clean up object URLs on unmount or file change
  useEffect(() => {
    return () => {
      if (modalFilePreview) {
        URL.revokeObjectURL(modalFilePreview);
      }
    };
  }, [modalFilePreview]);

  const handleOpenUploadModal = () => {
    setModalFile(null);
    if (modalFilePreview) URL.revokeObjectURL(modalFilePreview);
    setModalFilePreview(null);
    setModalAssetName('');
    setModalTags([]);
    setModalTagInput('');
    setModalError('');
    setShowUploadModal(true);
  };

  const handleCloseUploadModal = () => {
    if (uploading) return;
    setShowUploadModal(false);
    setModalFile(null);
    if (modalFilePreview) URL.revokeObjectURL(modalFilePreview);
    setModalFilePreview(null);
    setModalAssetName('');
    setModalTags([]);
    setModalTagInput('');
    setModalError('');
  };

  const handleFileSelection = (files) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    setModalFile(file);
    setModalError('');

    // Pre-populate asset name if empty or pristine
    const cleanName = file.name.replace(/\.[^/.]+$/, "");
    setModalAssetName(cleanName);

    // Create preview if image
    if (modalFilePreview) URL.revokeObjectURL(modalFilePreview);
    if (file.type && file.type.startsWith('image/')) {
      try {
        const previewUrl = URL.createObjectURL(file);
        setModalFilePreview(previewUrl);
      } catch (_) {
        setModalFilePreview(null);
      }
    } else {
      setModalFilePreview(null);
    }
  };

  const handleModalDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setModalDragActive(true);
    } else if (e.type === "dragleave") {
      setModalDragActive(false);
    }
  };

  const handleModalDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setModalDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelection(e.dataTransfer.files);
    }
  };

  const handleAddTag = (tagToAdd) => {
    const trimmed = (tagToAdd || modalTagInput).trim();
    if (!trimmed) return;
    if (!modalTags.includes(trimmed)) {
      setModalTags([...modalTags, trimmed]);
    }
    setModalTagInput('');
  };

  const handleRemoveTag = (tagToRemove) => {
    setModalTags(modalTags.filter(t => t !== tagToRemove));
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleExecuteUpload = async (e) => {
    if (e) e.preventDefault();
    if (!modalFile) {
      setModalError("Please select a file to upload.");
      return;
    }

    setUploading(true);
    setModalError('');
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('file', modalFile);
    if (modalAssetName.trim()) {
      formData.append('display_name', modalAssetName.trim());
    }
    if (modalTags.length > 0) {
      formData.append('tags', modalTags.join(', '));
    }

    let url = `${API_BASE}/api/upload?`;
    const params = new URLSearchParams();
    if (selectedUserContext && selectedUserContext !== 'self') {
      params.append('associated_user_id', selectedUserContext);
    }
    if (selectedTenant) {
      params.append('tenant_id', selectedTenant);
    }
    url += params.toString();

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Upload failed");

      setSuccess(`Asset "${modalAssetName.trim() || modalFile.name}" uploaded successfully!`);
      handleCloseUploadModal();
      fetchAssets();
      if (typeof window !== 'undefined' && window.fetchStudioAssets) {
        window.fetchStudioAssets();
      }
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setModalError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const toggleShare = (id) => {
    setError('');
    fetch(`${API_BASE}/api/upload/${id}/share`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Tenant-Domain': window.location.hostname
      }
    })
      .then(res => {
        if (!res.ok) throw new Error("Failed to change sharing status");
        return res.json();
      })
      .then(() => {
        fetchAssets();
      })
      .catch(err => setError(err.message));
  };

  const handleDelete = async (id, filename) => {
    const confirmed = await modal.confirm(`Are you sure you want to delete this asset?`, {
      title: 'Delete Asset',
      confirmText: 'Delete Asset',
      danger: true
    });
    if (!confirmed) return;
    setError('');
    fetch(`${API_BASE}/api/upload/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Tenant-Domain': window.location.hostname
      }
    })
      .then(res => {
        if (!res.ok) throw new Error("Failed to delete asset");
        setSuccess("Asset deleted successfully.");
        fetchAssets();
        setTimeout(() => setSuccess(''), 3000);
      })
      .catch(err => setError(err.message));
  };

  const filteredAssets = assets.filter(asset => {
    const searchTarget = `${asset.display_name || ''} ${asset.original_name || ''} ${asset.tags || ''}`.toLowerCase();
    const matchesSearch = searchTarget.includes(searchQuery.toLowerCase());

    if (!matchesSearch) return false;

    const mime = (asset.mime_type || '').toLowerCase();
    if (selectedType === 'all') return true;
    if (selectedType === 'images') return mime.startsWith('image/') && mime !== 'image/svg+xml';
    if (selectedType === 'svgs') return mime === 'image/svg+xml';
    if (selectedType === 'documents') return mime.includes('pdf') || mime.includes('text') || mime.includes('doc') || mime.includes('sheet');
    if (selectedType === 'other') {
      const isImg = mime.startsWith('image/') && mime !== 'image/svg+xml';
      const isSvg = mime === 'image/svg+xml';
      const isDoc = mime.includes('pdf') || mime.includes('text') || mime.includes('doc') || mime.includes('sheet');
      return !isImg && !isSvg && !isDoc;
    }
    return true;
  });

  const getFileIcon = (mimeType) => {
    const mime = (mimeType || '').toLowerCase();
    if (mime.includes('pdf')) return '📕';
    if (mime.includes('text') || mime.includes('doc')) return '📄';
    if (mime.includes('sheet') || mime.includes('xls')) return '📊';
    if (mime.includes('zip') || mime.includes('tar') || mime.includes('rar')) return '📦';
    return '📁';
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    alert('Asset URL copied to clipboard!');
  };

  const downloadAsset = async (assetOrUrl, filenameOrFallback) => {
    setError('');
    let assetId = null;
    let filename = 'download';
    let fallbackUrl = '';

    if (typeof assetOrUrl === 'object' && assetOrUrl !== null) {
      assetId = assetOrUrl.id;
      filename = assetOrUrl.original_name || assetOrUrl.filename || 'download';
      fallbackUrl = filenameOrFallback || '';
    } else {
      fallbackUrl = assetOrUrl || '';
      filename = filenameOrFallback || 'download';
    }

    const base = API_BASE ? API_BASE.replace(/\/+$/, '') : '';
    const primaryUrl = assetId ? `${base}/api/upload/${assetId}/download` : fallbackUrl;

    try {
      let response = await fetch(primaryUrl, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (!response.ok && fallbackUrl && primaryUrl !== fallbackUrl) {
        response = await fetch(fallbackUrl, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
      }

      if (!response.ok) throw new Error("Failed to download file from server");
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(link.href), 1000);
      setSuccess(`File "${filename}" downloaded successfully.`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || "Failed to download file from server");
    }
  };

  return (
    <div style={{
      background: 'var(--panel-color, #161b22)',
      border: '1px solid var(--border-color, #30363d)',
      borderRadius: '12px',
      padding: '24px',
      display: 'flex',
      flexDirection: 'column',
      gap: '20px'
    }}>
      {/* 1. Header Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--text-main, #c9d1d9)' }}>Design Assets Library</h3>
          <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--text-mute, #8b949e)' }}>Upload, preview, share and manage custom coin faces, logos, backgrounds and brag page media.</p>
        </div>

        <div>
          <button
            type="button"
            onClick={handleOpenUploadModal}
            style={{
              backgroundColor: 'var(--primary-color, #2563eb)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 20px',
              fontSize: '13px',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.15s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.filter = 'brightness(1.1)'}
            onMouseOut={(e) => e.currentTarget.style.filter = 'none'}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span>Upload New Asset</span>
          </button>
        </div>
      </div>

      {/* Main Panel Alerts */}
      {success && (
        <div style={{ backgroundColor: 'rgba(46, 160, 67, 0.15)', border: '1px solid rgba(46, 160, 67, 0.4)', borderRadius: '8px', color: '#3fb950', padding: '12px 18px', fontSize: '13px', fontWeight: '600' }}>
          ✅ {success}
        </div>
      )}
      {error && (
        <div style={{ backgroundColor: 'rgba(248, 81, 73, 0.15)', border: '1px solid rgba(248, 81, 73, 0.4)', borderRadius: '8px', color: '#ff7b72', padding: '12px 18px', fontSize: '13px', fontWeight: '600' }}>
          ⚠️ {error}
        </div>
      )}

      {/* 2. Search & Category Filters */}
      <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: '240px', position: 'relative' }}>
          <input
            type="text"
            placeholder="🔍 Search assets by name or tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              backgroundColor: 'var(--input-bg, #0d1117)',
              border: '1px solid var(--border-color, #30363d)',
              borderRadius: '6px',
              padding: '10px 12px 10px 36px',
              color: 'var(--input-text, var(--text-main, #f0f6fc))',
              fontSize: '13px',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '6px', overflowX: 'auto' }}>
          {[
            { key: 'all', label: 'All Files' },
            { key: 'images', label: '🖼️ Images' },
            { key: 'svgs', label: '🪙 SVGs' },
            { key: 'documents', label: '📄 Documents' },
            { key: 'other', label: '⚙️ Others' }
          ].map((type) => {
            const isSelected = selectedType === type.key;
            return (
              <button
                key={type.key}
                type="button"
                onClick={() => setSelectedType(type.key)}
                style={{
                  backgroundColor: isSelected ? 'var(--primary-color, #2563eb)' : 'var(--card-bg, #21262d)',
                  color: isSelected ? '#ffffff' : 'var(--text-main, #f0f6fc)',
                  border: isSelected ? '1px solid var(--primary-color, #2563eb)' : '1px solid var(--border-color, #30363d)',
                  borderRadius: '6px',
                  padding: '8px 14px',
                  fontSize: '12px',
                  fontWeight: isSelected ? '700' : '600',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  boxShadow: isSelected ? '0 2px 8px rgba(0, 0, 0, 0.25)' : 'none',
                  transition: 'all 0.15s ease'
                }}
                onMouseOver={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.borderColor = 'var(--text-main, #f0f6fc)';
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                  }
                }}
                onMouseOut={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.borderColor = 'var(--border-color, #30363d)';
                    e.currentTarget.style.backgroundColor = 'var(--card-bg, #21262d)';
                  }
                }}
              >
                {type.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* 3. Assets Cards Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-mute)' }}>Loading assets...</div>
      ) : filteredAssets.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-mute)', border: '1px solid var(--border-color)', borderRadius: '10px', background: 'var(--card-bg)' }}>
          <span style={{ fontSize: '36px' }}>📭</span>
          <p style={{ margin: '10px 0 0 0', fontSize: '14px', fontWeight: '600' }}>No assets found</p>
          <p style={{ margin: '4px 0 0 0', fontSize: '12px' }}>Try uploading a file or adjusting your filters.</p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '16px'
        }}>
          {filteredAssets.map((asset) => {
            const isImage = (asset.mime_type || '').toLowerCase().startsWith('image/') && (asset.mime_type || '').toLowerCase() !== 'image/svg+xml';
            const isSvg = (asset.mime_type || '').toLowerCase() === 'image/svg+xml';
            const cleanLocation = asset.location && asset.location.includes('/uploads/')
              ? asset.location.substring(asset.location.indexOf('/uploads/'))
              : (asset.location || `/uploads/${asset.filename}`);
            const base = API_BASE ? API_BASE.replace(/\/+$/, '') : '';
            const fullyQualifiedUrl = `${base}${cleanLocation}`;

            const isGlobal = user?.role === 'GLOBAL_ADMIN' || user?.role === 'GLOBAL_POWER' || user?.role === 'GLOBAL_STANDARD';
            const isTenantAdmin = user?.role === 'TENANT_ADMIN' && asset.tenant_id === user?.tenant_id;
            const isOwner = asset.created_by_id === user?.id || asset.associated_user_id === user?.id;
            const canShareOrDelete = isGlobal || isTenantAdmin || isOwner;
            const canDownload = isGlobal || isTenantAdmin || isOwner || asset.is_shared;

            const displayName = asset.display_name || asset.original_name;
            const tagList = asset.tags ? asset.tags.split(',').map(t => t.trim()).filter(Boolean) : [];

            return (
              <div
                key={asset.id}
                style={{
                  background: 'var(--card-bg, #161b22)',
                  border: '1px solid var(--border-color, #30363d)',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.1)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                }}
              >
                <div
                  onMouseEnter={() => setHoveredAssetId(asset.id)}
                  onMouseLeave={() => setHoveredAssetId(null)}
                  style={{
                    height: '130px',
                    backgroundColor: '#0a0f1d',
                    borderBottom: '1px solid var(--border-color, #30363d)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative'
                  }}
                >
                  {isImage || isSvg ? (
                    <img
                      src={fullyQualifiedUrl}
                      alt={displayName}
                      loading="lazy"
                      onError={(e) => {
                        if (!e.currentTarget.dataset.retried && asset.id && token) {
                          e.currentTarget.dataset.retried = 'true';
                          fetch(`${base}/api/upload/${asset.id}/download`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                          })
                            .then(r => r.ok ? r.blob() : null)
                            .then(b => {
                              if (b && e.currentTarget) {
                                e.currentTarget.src = URL.createObjectURL(b);
                              }
                            })
                            .catch(() => {});
                        }
                      }}
                      style={{
                        maxHeight: '100%',
                        maxWidth: '100%',
                        objectFit: 'contain',
                        padding: '8px'
                      }}
                    />
                  ) : (
                    <span style={{ fontSize: '48px' }}>{getFileIcon(asset.mime_type)}</span>
                  )}

                  <span style={{
                    position: 'absolute',
                    top: '8px',
                    left: '8px',
                    backgroundColor: asset.is_shared ? 'rgba(46, 160, 67, 0.9)' : 'rgba(33, 38, 45, 0.9)',
                    color: '#fff',
                    fontSize: '9px',
                    fontWeight: '700',
                    padding: '3px 6px',
                    borderRadius: '4px',
                    border: '1px solid rgba(255,255,255,0.1)'
                  }}>
                    {asset.is_shared ? '📢 SHARED' : '🔒 PRIVATE'}
                  </span>

                  {hoveredAssetId === asset.id && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      backgroundColor: 'rgba(10, 15, 29, 0.95)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      padding: '12px',
                      boxSizing: 'border-box',
                      color: '#fff',
                      fontSize: '11px',
                      textAlign: 'left',
                      gap: '8px',
                      backdropFilter: 'blur(4px)',
                      pointerEvents: 'none'
                    }}>
                      <div>
                        <span style={{ fontWeight: '700', color: 'var(--primary-color, #bdb2ff)', display: 'block', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Uploader</span>
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', fontSize: '11px', color: '#e6edf3' }}>{asset.creator_email}</span>
                      </div>
                      <div>
                        <span style={{ fontWeight: '700', color: '#58a6ff', display: 'block', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Owner Context</span>
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', fontSize: '11px', color: '#e6edf3' }}>{asset.owner_email}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, justifyContent: 'space-between' }}>
                  <div>
                    <div
                      title={displayName}
                      style={{
                        fontSize: '13px',
                        fontWeight: '700',
                        color: 'var(--text-main, #c9d1d9)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {displayName}
                    </div>

                    {asset.display_name && asset.display_name !== asset.original_name && (
                      <div
                        title={`File: ${asset.original_name}`}
                        style={{
                          fontSize: '10px',
                          color: 'var(--text-mute, #8b949e)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          marginTop: '2px'
                        }}
                      >
                        {asset.original_name}
                      </div>
                    )}

                    {/* Tag Badges */}
                    {tagList.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                        {tagList.map((tag, idx) => (
                          <span
                            key={idx}
                            style={{
                              fontSize: '9px',
                              fontWeight: '600',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              backgroundColor: 'rgba(189, 178, 255, 0.12)',
                              color: 'var(--primary-color, #bdb2ff)',
                              border: '1px solid rgba(189, 178, 255, 0.25)'
                            }}
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <div style={{ fontSize: '10px', color: 'var(--text-mute, #8b949e)', marginTop: '4px' }}>
                      {formatBytes(asset.file_size)} • {(asset.mime_type || '').split('/').pop().toUpperCase()}
                      {asset.width && asset.height && (
                        <span> • {asset.width}x{asset.height}px {asset.dpi ? `(${asset.dpi} DPI)` : ''}</span>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(fullyQualifiedUrl)}
                        title="Copy Asset URL Link"
                        style={{
                          flex: 1,
                          background: 'rgba(56, 189, 248, 0.12)',
                          color: '#38bdf8',
                          border: '1px solid rgba(56, 189, 248, 0.35)',
                          borderRadius: '6px',
                          padding: '7px 10px',
                          fontSize: '11px',
                          fontWeight: '700',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '5px',
                          transition: 'all 0.15s ease'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.background = 'rgba(56, 189, 248, 0.22)';
                          e.currentTarget.style.borderColor = '#38bdf8';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.background = 'rgba(56, 189, 248, 0.12)';
                          e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.35)';
                        }}
                      >
                        <span>🔗</span>
                        <span>Copy Link</span>
                      </button>
                      {canShareOrDelete && (
                        <button
                          type="button"
                          onClick={() => handleDelete(asset.id, displayName)}
                          title="Delete Asset"
                          style={{
                            backgroundColor: 'rgba(239, 68, 68, 0.12)',
                            color: '#ef4444',
                            border: '1px solid rgba(239, 68, 68, 0.4)',
                            borderRadius: '6px',
                            padding: '7px 10px',
                            fontSize: '12px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.15s ease'
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.25)';
                            e.currentTarget.style.borderColor = '#ef4444';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.12)';
                            e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.4)';
                          }}
                        >
                          🗑️
                        </button>
                      )}
                    </div>

                    {canDownload && (
                      <button
                        type="button"
                        onClick={() => downloadAsset(asset, fullyQualifiedUrl)}
                        title="Download Asset File"
                        style={{
                          background: 'rgba(255, 255, 255, 0.08)',
                          color: 'var(--text-main, #f0f6fc)',
                          border: '1px solid var(--border-color, #30363d)',
                          borderRadius: '6px',
                          padding: '7px 10px',
                          fontSize: '11px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          transition: 'all 0.15s ease',
                          width: '100%',
                          textAlign: 'center'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                          e.currentTarget.style.borderColor = 'var(--text-main, #f0f6fc)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                          e.currentTarget.style.borderColor = 'var(--border-color, #30363d)';
                        }}
                      >
                        <span>📥</span>
                        <span>Download Asset</span>
                      </button>
                    )}

                    {canShareOrDelete && (
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        fontSize: '11px',
                        fontWeight: '600',
                        color: 'var(--text-main, #f0f6fc)',
                        padding: '6px 8px',
                        background: 'rgba(255, 255, 255, 0.04)',
                        border: '1px solid var(--border-color, #30363d)',
                        borderRadius: '6px'
                      }}>
                        <span>Share in Tenant:</span>
                        <input
                          type="checkbox"
                          checked={Boolean(asset.is_shared)}
                          onChange={() => toggleShare(asset.id)}
                          style={{ cursor: 'pointer', width: '13px', height: '13px' }}
                        />
                      </label>
                    )}
                  </div>
                </div>

              </div>
            );
          })}
        </div>
      )}

      {/* 4. Upload Popup Modal (Portal) */}
      {showUploadModal && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="upload-modal-title"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(5px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100000,
            padding: '20px'
          }}
          onClick={handleCloseUploadModal}
        >
          <div
            style={{
              background: 'var(--card-bg, #161b22)',
              border: '1px solid var(--border-color, #30363d)',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '520px',
              maxHeight: '92vh',
              overflowY: 'auto',
              boxShadow: '0 20px 48px rgba(0, 0, 0, 0.6)',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '20px 24px',
                borderBottom: '1px solid var(--border-color, #30363d)'
              }}
            >
              <div>
                <h3
                  id="upload-modal-title"
                  style={{
                    margin: 0,
                    fontSize: '18px',
                    fontWeight: '700',
                    color: 'var(--primary-color, #bdb2ff)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  <span>Upload Asset</span>
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-mute, #8b949e)' }}>
                  Assign a custom name, tags, and select an asset file to upload.
                </p>
              </div>

              <button
                type="button"
                onClick={handleCloseUploadModal}
                disabled={uploading}
                aria-label="Close"
                style={{
                  background: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid var(--border-color, #30363d)',
                  color: 'var(--text-main, #f0f6fc)',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  padding: '6px 10px',
                  borderRadius: '6px',
                  lineHeight: 1,
                  transition: 'all 0.15s ease'
                }}
                onMouseOver={(e) => {
                  if (!uploading) e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                }}
                onMouseOut={(e) => {
                  if (!uploading) e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)';
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Form Body */}
            <form onSubmit={handleExecuteUpload} style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {modalError && (
                <div style={{ backgroundColor: 'rgba(248, 81, 73, 0.15)', border: '1px solid rgba(248, 81, 73, 0.4)', borderRadius: '8px', color: '#ff7b72', padding: '10px 14px', fontSize: '13px', fontWeight: '600' }}>
                  ⚠️ {modalError}
                </div>
              )}

              {/* 1. File Selection / Dropzone */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-main, #c9d1d9)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  File <span style={{ color: '#ff7b72' }}>*</span>
                </label>

                <input
                  type="file"
                  ref={modalFileInputRef}
                  id="modal-asset-file-input"
                  style={{ display: 'none' }}
                  onChange={(e) => handleFileSelection(e.target.files)}
                />

                {!modalFile ? (
                  <div
                    onDragEnter={handleModalDrag}
                    onDragOver={handleModalDrag}
                    onDragLeave={handleModalDrag}
                    onDrop={handleModalDrop}
                    onClick={() => modalFileInputRef.current?.click()}
                    style={{
                      border: modalDragActive ? '2px dashed var(--primary-color, #bdb2ff)' : '2px dashed var(--border-color, #30363d)',
                      backgroundColor: modalDragActive ? 'rgba(189, 178, 255, 0.08)' : 'var(--input-bg, #0d1117)',
                      borderRadius: '10px',
                      padding: '30px 20px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <span style={{ fontSize: '32px' }}>📤</span>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main, #c9d1d9)' }}>
                      Drag and drop your file here, or click to browse
                    </span>
                    <span style={{ fontSize: '11px', color: 'var(--text-mute, #8b949e)' }}>
                      Supports PNG, JPG, SVG vectors, PDFs and document files
                    </span>
                  </div>
                ) : (
                  <div
                    style={{
                      border: '1px solid var(--border-color, #30363d)',
                      borderRadius: '8px',
                      padding: '14px 16px',
                      backgroundColor: 'var(--input-bg, #0d1117)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', overflow: 'hidden' }}>
                      {modalFilePreview ? (
                        <img
                          src={modalFilePreview}
                          alt="preview"
                          style={{
                            width: '44px',
                            height: '44px',
                            objectFit: 'contain',
                            borderRadius: '6px',
                            backgroundColor: '#0a0f1d',
                            border: '1px solid var(--border-color, #30363d)'
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: '44px',
                            height: '44px',
                            borderRadius: '6px',
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '22px'
                          }}
                        >
                          {getFileIcon(modalFile.type)}
                        </div>
                      )}

                      <div style={{ overflow: 'hidden' }}>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main, #c9d1d9)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {modalFile.name}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-mute, #8b949e)', marginTop: '2px' }}>
                          {formatBytes(modalFile.size)} • {modalFile.type || 'Unknown Type'}
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => modalFileInputRef.current?.click()}
                      disabled={uploading}
                      style={{
                        padding: '7px 14px',
                        borderRadius: '6px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid var(--border-color, #30363d)',
                        color: 'var(--text-main, #f0f6fc)',
                        fontSize: '11px',
                        fontWeight: '600',
                        cursor: uploading ? 'not-allowed' : 'pointer',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.15s ease'
                      }}
                      onMouseOver={(e) => {
                        if (!uploading) e.currentTarget.style.background = 'rgba(255, 255, 255, 0.18)';
                      }}
                      onMouseOut={(e) => {
                        if (!uploading) e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                      }}
                    >
                      Change File
                    </button>
                  </div>
                )}
              </div>

              {/* 2. Asset Name Input */}
              <div>
                <label
                  htmlFor="modal-asset-name-input"
                  style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-main, #c9d1d9)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}
                >
                  Asset Name <span style={{ color: '#ff7b72' }}>*</span>
                </label>
                <input
                  id="modal-asset-name-input"
                  type="text"
                  placeholder="e.g. Front Gold Emblem High-Res"
                  value={modalAssetName}
                  onChange={(e) => setModalAssetName(e.target.value)}
                  disabled={uploading}
                  required
                  style={{
                    width: '100%',
                    backgroundColor: 'var(--input-bg, #0d1117)',
                    border: '1px solid var(--border-color, #30363d)',
                    borderRadius: '6px',
                    padding: '10px 14px',
                    color: 'var(--text-main, #c9d1d9)',
                    fontSize: '13px',
                    boxSizing: 'border-box'
                  }}
                />
                <span style={{ fontSize: '11px', color: 'var(--text-mute, #8b949e)', marginTop: '4px', display: 'block' }}>
                  Custom descriptive label shown across coin templates and asset pickers.
                </span>
              </div>

              {/* 3. Tags Input & Suggestions */}
              <div>
                <label
                  htmlFor="modal-asset-tag-input"
                  style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-main, #c9d1d9)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}
                >
                  Tags
                </label>

                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input
                    id="modal-asset-tag-input"
                    type="text"
                    placeholder="Add a tag and press Enter..."
                    value={modalTagInput}
                    onChange={(e) => setModalTagInput(e.target.value)}
                    onKeyDown={handleTagKeyDown}
                    disabled={uploading}
                    style={{
                      flex: 1,
                      backgroundColor: 'var(--input-bg, #0d1117)',
                      border: '1px solid var(--border-color, #30363d)',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      color: 'var(--text-main, #c9d1d9)',
                      fontSize: '13px',
                      boxSizing: 'border-box'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => handleAddTag()}
                    disabled={!modalTagInput.trim() || uploading}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      background: modalTagInput.trim() ? 'var(--primary-color, #2563eb)' : 'rgba(255, 255, 255, 0.06)',
                      border: modalTagInput.trim() ? '1px solid transparent' : '1px solid var(--border-color, #30363d)',
                      color: modalTagInput.trim() ? '#ffffff' : 'var(--text-mute, #8b949e)',
                      fontSize: '12px',
                      fontWeight: '700',
                      cursor: !modalTagInput.trim() || uploading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    + Add
                  </button>
                </div>

                {/* Active Tags Chips */}
                {modalTags.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                    {modalTags.map((tag) => (
                      <span
                        key={tag}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '3px 8px',
                          borderRadius: '12px',
                          backgroundColor: 'rgba(189, 178, 255, 0.15)',
                          color: 'var(--primary-color, #bdb2ff)',
                          border: '1px solid rgba(189, 178, 255, 0.3)',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}
                      >
                        #{tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          disabled={uploading}
                          aria-label={`Remove tag ${tag}`}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--primary-color, #bdb2ff)',
                            fontSize: '11px',
                            cursor: 'pointer',
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          ✕
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Suggested Tag Pills */}
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-mute, #8b949e)', display: 'block', marginBottom: '6px' }}>
                    Suggested tags:
                  </span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {SUGGESTED_TAGS.map((tag) => {
                      const isSelected = modalTags.includes(tag);
                      return (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => isSelected ? handleRemoveTag(tag) : handleAddTag(tag)}
                          disabled={uploading}
                          style={{
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: isSelected ? '700' : '600',
                            border: isSelected ? '1px solid var(--primary-color, #2563eb)' : '1px solid var(--border-color, #30363d)',
                            backgroundColor: isSelected ? 'var(--primary-color, #2563eb)' : 'rgba(255, 255, 255, 0.06)',
                            color: isSelected ? '#ffffff' : 'var(--text-main, #f0f6fc)',
                            cursor: uploading ? 'not-allowed' : 'pointer',
                            transition: 'all 0.15s ease'
                          }}
                          onMouseOver={(e) => {
                            if (!isSelected && !uploading) {
                              e.currentTarget.style.borderColor = 'var(--text-main, #f0f6fc)';
                              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.12)';
                            }
                          }}
                          onMouseOut={(e) => {
                            if (!isSelected && !uploading) {
                              e.currentTarget.style.borderColor = 'var(--border-color, #30363d)';
                              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.06)';
                            }
                          }}
                        >
                          {isSelected ? `✓ ${tag}` : `+ ${tag}`}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Modal Actions Footer */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: '10px',
                  marginTop: '10px',
                  paddingTop: '16px',
                  borderTop: '1px solid var(--border-color, #30363d)'
                }}
              >
                <button
                  type="button"
                  onClick={handleCloseUploadModal}
                  disabled={uploading}
                  style={{
                    padding: '8px 18px',
                    borderRadius: '6px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid var(--border-color, #30363d)',
                    color: 'var(--text-main, #f0f6fc)',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseOver={(e) => {
                    if (!uploading) e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
                  }}
                  onMouseOut={(e) => {
                    if (!uploading) e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                  }}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={!modalFile || uploading}
                  style={{
                    padding: '8px 22px',
                    borderRadius: '6px',
                    background: (!modalFile || uploading) ? 'rgba(37, 99, 235, 0.35)' : 'var(--primary-gradient, var(--primary-color, #2563eb))',
                    border: 'none',
                    color: '#ffffff',
                    fontSize: '13px',
                    fontWeight: '700',
                    cursor: (!modalFile || uploading) ? 'not-allowed' : 'pointer',
                    boxShadow: (!modalFile || uploading) ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    opacity: (!modalFile || uploading) ? 0.65 : 1,
                    transition: 'all 0.15s ease'
                  }}
                  onMouseOver={(e) => {
                    if (modalFile && !uploading) e.currentTarget.style.filter = 'brightness(1.1)';
                  }}
                  onMouseOut={(e) => {
                    if (modalFile && !uploading) e.currentTarget.style.filter = 'none';
                  }}
                >
                  {uploading ? (
                    <>
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <span>Upload Asset</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default AssetManager;
